import React, { ForwardedRef, forwardRef } from "react";

import clsx from "clsx";
import carouselStyles from "components/carousel/index.module.css";
import IndicatorDots from "components/carousel/indicator-dots";
import { withCarousel } from "components/carousel/use-carousel";
import Heading from "components/heading";
import Image from "next/image";
import AfterTheCloseImage from "public/images/enerflo-solar-customer-engagement-after-the-close.png";
import DuringTheProjectImage from "public/images/enerflo-solar-customer-engagement-during-the-project.png";
import FirstImpressionImage from "public/images/enerflo-solar-customer-engagement-first-impression.png";
import PresentationImage from "public/images/enerflo-solar-customer-engagement-presentation.png";

import Carousel, { NextButton, PrevButton, StepRendererProps } from "../../carousel";
import HeroSection from "../../hero-section";

const steps = [
  {
    title: "First Impression",
    description:
      "Help customers close themselves by making a great first impression. Share a unique link to your company-branded customer portal to start building trust immediately. Customize the portal to show your customers how your company is different (and better!) by embedding information, videos and other custom content & including other installs you’ve completed successfully. Automatically send appointment reminders and notifications after your first visit.",
    image: FirstImpressionImage,
    imageAlt: "Make a great first impression by giving every customer their own customer portal with Enerflo.",
  },
  {
    title: "Presentation",
    description:
      "Presentation is everything. Say goodbye to snooze-fest PDF proposals and engage your customers with Optimus, the proposal tool that’s raising the bar on the industry standard. Unlike the typical static PDF, Optimus serves each proposal in a stunning, interactive way that’s sure to impress. Integrate your favorite design tool. Personalize each proposal with annotations and notes for your customer. When it’s ready to share, automatically send it to their customer portal for easy reference. Share new messages with them to keep them engaged and moving forward. And if you have to, yes, you can export the proposal to PDF (if you gotta be old skool).",
    image: PresentationImage,
    imageAlt:
      "Wow your customers with Optimus, Enerflo’s Solar Proposal Tool that stands head and shoulders above the rest.",
  },
  {
    title: "After the Close",
    description: (
      <span>
        Once the deal is closed, the customer portal automagically transforms into a{" "}
        <span className="line-through">pizza</span> solar tracker, to give your customer a view of the project and
        what’s next. Personalize their experience with a branded welcome video that lets them know what to expect. Give
        them quick access to their signed documents or bill. They’ll love the visibility into project progression.
      </span>
    ),
    image: AfterTheCloseImage,
    imageAlt:
      "Once the deal is closed, the Enerflo Customer Portal transforms to give your customer a view of the project and what’s next.",
  },
  {
    title: "During the Install",
    description:
      "Keep your customer in the loop as milestones are reached. Have delays? Easily communicate those to your customers using our deal feed. Swap out their POC for a project manager. Allow your customer to reach out to their POCs via phone, email or chat. Automatically send notifications and updates as things progress to ease their concerns and keep them informed. All communications can be handled from within the customer portal.",
    image: DuringTheProjectImage,
    imageAlt:
      "The Enerflo Customer Portal enables communications between everyone involved in the project, creating transparency and efficiency.",
  },
];

const StepRenderer = forwardRef<HTMLDivElement, StepRendererProps>(
  ({ step, className }: StepRendererProps, ref: ForwardedRef<HTMLDivElement> | undefined) => (
    <div
      className={clsx(
        className,
        "inline-flex flex-col-reverse items-center space-y-4 space-y-reverse md:space-y-0 md:space-x-8 lg:space-x-28 md:flex-row"
      )}
      ref={ref}
    >
      <HeroSection.Content className="md:w-1/2 px-0">
        <HeroSection.Header className="mt-0 md:items-start">
          <Heading tag="h3">{step.title}</Heading>
        </HeroSection.Header>
        <HeroSection.Description className="text-white">{step.description}</HeroSection.Description>
      </HeroSection.Content>

      <figure className="w-full md:w-1/2">
        <Image
          src={step.image}
          alt={step.imageAlt}
          layout="responsive"
          quality={100}
          placeholder="blur"
          loading="eager"
          className="crispImage"
          sizes="(min-width: 768px) 50vw, 100vw"
        />
      </figure>
    </div>
  )
);
StepRenderer.displayName = "StepRenderer";

const TheProcess = () => (
  <div className="pt-10 bg-gradient-to-b from-white to-gray-100 relative z-0">
    {/* To fill the gap in between this and the next section */}
    <div className="absolute bottom-0 w-full h-[50%] z-0 bg-navy" />
    <div className="bg-navy pt-20 pb-10 transform skew-y-6">
      <HeroSection className="text-white -skew-y-6 lg:pt-8">
        <div className="w-full space-y-4 px-6 sm:px-10 lg:px-14">
          <div className="flex justify-center items-center space-x-4">
            <PrevButton className={clsx(carouselStyles.carouselButtonBlue, "!p-0 border-none")} />
            <IndicatorDots inactiveClassName="bg-gray-400" />
            <NextButton className={clsx(carouselStyles.carouselButtonBlue, "!p-0 border-none")} />
          </div>
          <Carousel StepRenderer={StepRenderer} />
        </div>
      </HeroSection>
    </div>
  </div>
);

export default withCarousel(TheProcess, { steps });
